import { Component, OnInit, OnDestroy } from '@angular/core';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { ProductManagementDialogComponent } from '@app/product-catalog-management/dialog/product-management-dialog/product-management-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Observable, throwError } from 'rxjs';
import { isEmpty } from 'lodash';
import { catchError, finalize, first, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { Router } from '@angular/router';
import { LampCatalogueStoreService } from '@app/shared/services/store/lamp-catalogue-store.service';
import { LuminaireCategory } from '@app/shared/models/luminaire-category/luminaire-category';
import { CountedLampDetails, LampDetails } from '@app/shared/models/lamp-catalogue/lamp-details';
import { HttpErrorResponse } from '@angular/common/http';
import { IProductManagementDialogComponent } from './models/product-management-dialog';

@Component({
  selector: 'app-product-catalog-management',
  templateUrl: './product-catalog-management.component.html',
  styleUrls: ['./product-catalog-management.component.scss'],
})
export class ProductCatalogManagementComponent implements OnInit, OnDestroy {
  protected ngUnsubscribe: Subject<void> = new Subject<void>();
  public catalog$?: Observable<LampDetails[]>;
  public maxRowCount = 0;
  public searchParam = '';
  public searchValue$: Subject<string> = new Subject<string>();
  private luminaireCategories: LuminaireCategory[] = [];
  private manufacturers: string[] = [];

  constructor(
    public overlayLoaderService: OverlayLoaderService,
    private lampCatalogueStoreService: LampCatalogueStoreService,
    private matDialog: MatDialog,
    private router: Router
  ) {}

  public ngOnInit(): void {
    this.catalog$ = this.lampCatalogueStoreService.lampCatalogue$.pipe(
      tap((catalogue: CountedLampDetails) => (this.maxRowCount = catalogue.maxRowCount)),
      map((catalogue: CountedLampDetails): LampDetails[] => catalogue.data)
    );

    this.lampCatalogueStoreService.manufacturers$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((res: string[]) => (this.manufacturers = res));

    this.lampCatalogueStoreService
      .getLuminaireCategories()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: LuminaireCategory[]) => (this.luminaireCategories = res));
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && this.searchParam.trim().length > 0) {
      this.lampCatalogueStoreService.getLamps(this.searchParam).pipe(takeUntil(this.ngUnsubscribe)).subscribe();
    }
  }

  public onSearchClick(value: string): void {
    this.overlayLoaderService.isLoading = true;
    value = value || '';
    this.lampCatalogueStoreService
      .getLamps(value)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => (this.overlayLoaderService.isLoading = false));
  }

  public manageLamp(lampDetails: LampDetails = {} as LampDetails): void {
    this.matDialog.open<ProductManagementDialogComponent, IProductManagementDialogComponent, any>(ProductManagementDialogComponent, {
      data: {
        title: 'Ein Produkt hinzufügen',
        confirmText: 'Speichern',
        declineText: 'Abbrechen',
        isEditMode: !isEmpty(lampDetails),
        lampDetails: lampDetails,
        manufacturers: this.manufacturers,
        luminaireCategories: this.luminaireCategories,
      },
      width: '780px',
      panelClass: ['-md', 'product-management-dialog'],
      id: 'productManagementDialog',
    });
  }

  public deleteLamp(lampDetails: LampDetails): void {
    this.matDialog
      .open(NotificationDialogComponent, {
        data: {
          descriptionText: 'Möchten Sie diese Leuchte wirklich aus dem Produktkatalog löschen?',
          confirmText: 'Ja',
          declineText: 'Nein',
        },
        panelClass: ['-md'],
      })
      .afterClosed()
      .subscribe((confirm) => {
        if (confirm) {
          this.overlayLoaderService.isLoading = true;
          this.lampCatalogueStoreService
            .deleteLampEntry(lampDetails.guid)
            .pipe(
              switchMap(() => this.lampCatalogueStoreService.getLamps()),
              catchError((err: HttpErrorResponse) => throwError(() => err)),
              finalize(() => (this.overlayLoaderService.isLoading = false)),
              first()
            )
            .subscribe(() => (this.overlayLoaderService.isLoading = false));
        }
      });
  }

  public gotoDashboard(): void {
    this.router.navigateByUrl('/instrumententafel');
  }
}
